import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CRMPermissionsService } from "@/services/crm-permissions";
import { CRMObject, ObjectProfilePermission } from "@/types/crm/permissions";
import * as LucideIcons from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { FieldsConfigModal } from "./fields-config-modal";
import { PermissionModal } from "./permission-modal";

const PROFILES = [
    { value: "marketing", label: "Marketing" },
    { value: "finance", label: "Finance" },
    { value: "teacher", label: "Teacher" },
    { value: "prod_editor", label: "Prod editor" },
    { value: "prod_director", label: "Prod director" },
    { value: "prod_project_manager", label: "Prod project manager" },
    { value: "sales_representative", label: "Sales representative" },
    { value: "sales_supervisor", label: "Sales supervisor" },
    { value: "sales_manager", label: "Sales manager" },
    { value: "csm", label: "CSM" },
    { value: "support", label: "Support" },
    { value: "sales_admin", label: "Sales admin" },
    { value: "system_admin", label: "System admin" },
    { value: "default", label: "Default" }
];

const PERMISSION_OPTIONS = [
    { value: "private", label: "Private", color: "bg-blue-100 text-blue-800" },
    { value: "public", label: "Public", color: "bg-green-100 text-green-800" },
    { value: "none", label: "None", color: "bg-gray-100 text-gray-800" }
];

interface PermissionRowProps {
    permission: ObjectProfilePermission;
    referenceTypeId: number;
    onUpdate: () => void;
    objectData?: CRMObject;
    viewMode?: "by_object" | "by_profile";
}

export function PermissionRow({ permission, referenceTypeId, onUpdate, objectData, viewMode = "by_object" }: PermissionRowProps) {
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [showFieldsModal, setShowFieldsModal] = useState(false);
    const [updatingFields, setUpdatingFields] = useState(false);

    const getPermissionBadge = (value: string) => {
        const option = PERMISSION_OPTIONS.find(opt => opt.value === value);
        return (
            <Badge variant="secondary" className={option?.color}>
                {option?.label}
            </Badge>
        );
    };

    const getFieldsDisplayText = () => {
        // Vérification de sécurité pour éviter l'erreur si fields est undefined
        if (!permission.fields || !Array.isArray(permission.fields)) {
            return "0 fields";
        }

        if (permission.fields.includes("*")) {
            return `All (${objectData?.fields.length || 0})`;
        }
        return `${permission.fields.length} fields`;
    };

    // Fonction pour mettre à jour les champs
    const handleFieldsUpdate = async (fields: string[]) => {
        if (permission.id === 0) {
            toast.error("Cannot update a default permission");
            return;
        }

        setUpdatingFields(true);
        try {
            await CRMPermissionsService.updatePermissionFields(permission.id, fields);
            toast.success("Fields updated successfully");
            onUpdate();
        } catch (error) {
            toast.error("Error updating fields");
        } finally {
            setUpdatingFields(false);
        }
    };

    // Sécuriser les champs pour la modale
    const safeFields = permission.fields && Array.isArray(permission.fields) ? permission.fields : [];

    const IconComponent = objectData?.icon && (LucideIcons as any)[objectData.icon];
    const IconColor = objectData?.color || "bg-gray-500";

    return (
        <tr className="border-b hover:bg-gray-50">
            {/* Profile or Object */}
            <td className="px-4 py-2 text-sm font-medium">
                {viewMode === "by_object" ? (
                    PROFILES.find(p => p.value === permission.profile)?.label || permission.profile
                ) : (
                    <div className="flex items-center gap-2">
                        <div
                            className={cn("flex h-6 w-6 flex-shrink-0 items-center justify-center rounded")}
                            style={{ backgroundColor: IconColor }}
                        >
                            {IconComponent && <IconComponent className="h-4 w-4 text-white" strokeWidth={1.5} />}
                        </div>
                        <span>{objectData?.label || permission.reference_type}</span>
                    </div>
                )}
            </td>

            {/* Champs */}
            <td className="px-4 py-2 text-center">
                <Button size="sm" variant="outline" onClick={() => setShowFieldsModal(true)} className="h-8 text-xs" disabled={updatingFields || permission.id === 0}>
                    {updatingFields ? "..." : getFieldsDisplayText()}
                </Button>
            </td>

            {/* Liste */}
            <td className="px-4 py-2 text-center">{getPermissionBadge(permission.action_list || "none")}</td>

            {/* Détail */}
            <td className="px-4 py-2 text-center">{getPermissionBadge(permission.action_retrieve || "none")}</td>

            {/* Création */}
            <td className="px-4 py-2 text-center">{getPermissionBadge(permission.action_create || "none")}</td>

            {/* Modification */}
            <td className="px-4 py-2 text-center">{getPermissionBadge(permission.action_update || "none")}</td>

            {/* Suppression */}
            <td className="px-4 py-2 text-center">{getPermissionBadge(permission.action_delete || "none")}</td>

            {/* Actions */}
            <td className="px-4 py-2 text-center">
                <Button size="sm" variant="outline" onClick={() => setShowPermissionModal(true)}>
                    Update
                </Button>
            </td>

            {/* Modal de configuration des permissions */}
            <PermissionModal
                open={showPermissionModal}
                onOpenChange={setShowPermissionModal}
                permission={permission}
                referenceTypeId={referenceTypeId}
                onSuccess={onUpdate}
                selectedObjectName={objectData?.label || ""}
            />

            {/* Modal de configuration des champs */}
            {objectData && (
                <FieldsConfigModal
                    open={showFieldsModal}
                    onOpenChange={setShowFieldsModal}
                    currentFields={safeFields}
                    allFields={objectData.fields}
                    onFieldsChange={handleFieldsUpdate}
                    profile={permission.profile}
                    referenceTypeId={referenceTypeId}
                />
            )}
        </tr>
    );
}
